import React, { useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useDevice } from '../hooks';
import { authAPI } from '../services/api';
import ProfileEditModal from './ProfileEditModal';
import ThemeToggle from './ThemeToggle';
import OfflineIndicator from './OfflineIndicator';
import { BottomTabBar } from './ios';
import {
  HiOutlineHome,
  HiOutlineSearch,
  HiOutlineFilm,
  HiOutlineVideoCamera,
  HiOutlineCollection,
  HiOutlineUsers,
  HiOutlineCog,
  HiOutlineLogout,
  HiOutlineMenu,
  HiOutlineX,
  HiOutlineChevronLeft,
  HiOutlineSparkles,
} from 'react-icons/hi';

function Layout({ children }) {
  const { t } = useTranslation();
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useDevice();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Adiciona classe para identificar quando está no app autenticado
  React.useEffect(() => {
    document.body.classList.add('app-layout-active');
    return () => {
      document.body.classList.remove('app-layout-active');
    };
  }, []);

  // Detecta se o bottom menu está realmente visível (< 769px)
  const isBottomMenuVisible = typeof window !== 'undefined' && window.innerWidth < 769;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const closeSidebar = () => setSidebarOpen(false);

  const handleProfileUpdate = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching updated user:', error);
    }
  };

  // Determina o título da página atual para o header mobile
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return t('navigation.home');
    if (path === '/search') return t('navigation.search');
    if (path === '/movies') return t('navigation.movies');
    if (path === '/series') return t('navigation.series');
    if (path.startsWith('/series/')) return t('navigation.series');
    if (path === '/admin/users') return t('navigation.users');
    if (path === '/admin/series') return t('navigation.series');
    if (path === '/admin/settings') return t('navigation.settings');
    if (path === '/profile') return t('navigation.profile');
    if (path === '/profile/edit') return t('profile.editProfile');
    if (path === '/top10') return t('navigation.top10') || 'Top 10';
    return t('auth.appName');
  };

  // Verifica se pode mostrar botão de voltar
  const canGoBack = location.pathname !== '/dashboard' && location.pathname !== '/movies' && location.pathname !== '/search' && location.pathname !== '/profile' && location.pathname !== '/top10' && !location.pathname.startsWith('/series');

  return (
    <div className={`app-container ${isMobile ? 'app-container--mobile' : ''}`}>
      {/* ==========================================
          MOBILE HEADER (só aparece em mobile)
          ========================================== */}
      {isMobile && (
        <div className="mobile-header ios-safe-area-top">
          <div className="mobile-header__left">
            {canGoBack ? (
              <button
                className="mobile-header__btn"
                onClick={() => navigate(-1)}
                aria-label="Go back"
              >
                <HiOutlineChevronLeft size={28} />
              </button>
            ) : !isBottomMenuVisible ? (
              <button
                className="mobile-header__btn"
                onClick={() => setSidebarOpen(true)}
                aria-label="Open menu"
              >
                <HiOutlineMenu size={24} />
              </button>
            ) : (
              <div style={{ width: '44px' }}></div>
            )}
          </div>
          
          <h1 className="mobile-header__title">{getPageTitle()}</h1>
          
          <div className="mobile-header__right">
            <ThemeToggle />
          </div>
        </div>
      )}

      {/* Offline Indicator */}
      {isMobile && <OfflineIndicator />}

      {/* ==========================================
          SIDEBAR OVERLAY (mobile)
          ========================================== */}
      {isMobile && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={closeSidebar}
        />
      )}

      {/* ==========================================
          SIDEBAR (desktop sempre, mobile quando aberto)
          ========================================== */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''} ${isMobile ? 'sidebar--mobile' : ''}`}>
        <div className="sidebar-logo">
          <h1>{t('auth.appName')}</h1>
          {isMobile && (
            <button
              className="menu-btn"
              onClick={closeSidebar}
              aria-label="Close menu"
            >
              <HiOutlineX size={24} />
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">{t('navigation.menu')}</div>
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <HiOutlineHome />
              <span>{t('navigation.home')}</span>
            </NavLink>
            <NavLink
              to="/search"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <HiOutlineSearch />
              <span>{t('navigation.search')}</span>
            </NavLink>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">{t('navigation.myList')}</div>
            <NavLink
              to="/movies"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <HiOutlineFilm />
              <span>{t('navigation.movies')}</span>
            </NavLink>
            <NavLink
              to="/series"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <HiOutlineCollection />
              <span>{t('navigation.series')}</span>
            </NavLink>
            <NavLink
              to="/top10"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              onClick={closeSidebar}
            >
              <HiOutlineSparkles />
              <span>{t('navigation.top10') || 'Top 10'}</span>
            </NavLink>
          </div>

          {user?.is_admin && (
            <div className="nav-section">
              <div className="nav-section-title">{t('navigation.administration')}</div>
              <NavLink
                to="/admin/users"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <HiOutlineUsers />
                <span>{t('navigation.users')}</span>
              </NavLink>
              <NavLink
                to="/admin/series"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <HiOutlineVideoCamera />
                <span>{t('navigation.series')}</span>
              </NavLink>
              <NavLink
                to="/admin/settings"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                onClick={closeSidebar}
              >
                <HiOutlineCog />
                <span>{t('navigation.settings')}</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div
            className="user-info"
            onClick={() => {
              setShowProfileModal(true);
              closeSidebar();
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="user-avatar">
              {user?.username?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h4>{user?.username}</h4>
              <span>{user?.is_admin ? t('navigation.administrator') : t('navigation.user')}</span>
            </div>
          </div>
          <button
            className="nav-link"
            onClick={handleLogout}
            style={{ marginTop: '12px', width: '100%' }}
          >
            <HiOutlineLogout />
            <span>{t('navigation.logout')}</span>
          </button>
        </div>
      </aside>

      {/* ==========================================
          THEME TOGGLE - Desktop only
          ========================================== */}
      {!isMobile && (
        <div className="desktop-theme-toggle">
          <ThemeToggle />
        </div>
      )}

      {/* ==========================================
          PROFILE EDIT MODAL
          ========================================== */}
      {showProfileModal && user && (
        <ProfileEditModal
          user={user}
          onClose={() => setShowProfileModal(false)}
          onUpdate={handleProfileUpdate}
        />
      )}

      {/* ==========================================
          MAIN CONTENT
          ========================================== */}
      <main className={`main-content ${isMobile ? 'main-content--mobile' : ''}`}>
        {children}
      </main>

      {/* ==========================================
          BOTTOM TAB BAR - Mobile only
          ========================================== */}
      {isMobile && <BottomTabBar />}
    </div>
  );
}

export default Layout;
